import React, { ChangeEvent, FormEvent, useState } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"
import useLearnerAuth from "../../auth/learner/useLearnerAuth"
import { LEARNER_DEFAULT_PATH } from "../../auth/learner/constants"
import {
  getLearnerApiErrorMessage,
  isValidEmail,
  resolveLearnerReturnPath,
} from "../../auth/learner/errors"
import LearnerField from "../../component/student/LearnerField"
import LearnerButton from "../../component/student/LearnerButton"
import "./StudentLogin.style.scss"

const StudentLogin = () => {
  const [form, setForm] = useState({ email: "", password: "" })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({})
  const [params] = useSearchParams()
  const { login } = useLearnerAuth()

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    if (error) setError("")
    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const validate = () => {
    const nextErrors: { email?: string; password?: string } = {}
    if (!form.email.trim()) nextErrors.email = "Email is required"
    else if (!isValidEmail(form.email)) nextErrors.email = "Enter a valid email address"
    if (!form.password) nextErrors.password = "Password is required"
    setFieldErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setLoading(true)
    setError("")
    try {
      const student = await login({
        email: form.email.trim().toLowerCase(),
        password: form.password,
      })
      toast.success(`Welcome back, ${student.first_name}!`)
      const next = resolveLearnerReturnPath(params.get("next"), LEARNER_DEFAULT_PATH)
      window.location.assign(next)
    } catch (err) {
      setError(getLearnerApiErrorMessage(err, "Login failed. Please check your credentials."))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div data-theme="learner" className="student-login">
      <div className="student-login__card">
        <h1 className="student-login__title">Sign in</h1>

        {error && (
          <div className="student-login__error" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="student-login__form" noValidate>
          <LearnerField
            id="email"
            label="Email Address"
            name="email"
            type="email"
            placeholder="learner@example.com"
            value={form.email}
            onChange={handleChange}
            error={fieldErrors.email}
            autoComplete="email"
            autoFocus
            disabled={loading}
          />

          <LearnerField
            id="password"
            label="Password"
            name="password"
            type="password"
            placeholder="••••••••"
            value={form.password}
            onChange={handleChange}
            error={fieldErrors.password}
            autoComplete="current-password"
            disabled={loading}
          />

          <LearnerButton
            type="submit"
            fullWidth
            loading={loading}
            loadingText="Signing in..."
          >
            Sign In
          </LearnerButton>
        </form>
      </div>
    </div>
  )
}

export default StudentLogin
